import { useEffect, useMemo, useState } from "react";
import { ClassSubjectsGrade, ClassTimetable, DayOfWeek, TimetableSlot } from "../types";
import { fetchClassSubjects } from "../services/classSubjectsService";
import { fetchClassTimetable, saveClassTimetable } from "../services/timetableService";

// The backend sends clash / rule reasons (e.g. "Mr. X is already in 6 - B on Mon P2") as a plain-text body.
const messageOf = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  return typeof data === "string" && data.trim() ? data : fallback;
};

export const cellKey = (day: DayOfWeek, periodId: number) => `${day}|${periodId}`;

const toMap = (slots: TimetableSlot[]): Record<string, number> =>
  Object.fromEntries(slots.map((slot) => [cellKey(slot.day, slot.periodId), slot.assignmentId]));

/**
 * The class picker (grades with their sections) and the picked class's timetable. Picks and clears
 * stay in `draft` (cell → assignment id) until `save`, which sends the class's whole week in one
 * request (all or nothing). Switching class with unsaved changes goes through `pendingClassId`.
 */
export const useClassTimetable = () => {
  const [grades, setGrades] = useState<ClassSubjectsGrade[]>([]);
  const [gradeSectionId, setGradeSectionId] = useState<number | null>(null);
  const [pendingClassId, setPendingClassId] = useState<number | null>(null);
  const [timetable, setTimetable] = useState<ClassTimetable | null>(null);
  const [draft, setDraft] = useState<Record<string, number>>({});
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoadingClasses(true);
      try {
        const data = await fetchClassSubjects();
        setGrades(data.grades);
        const first = data.grades.flatMap((grade) => grade.sections)[0];
        if (first) setGradeSectionId(first.gradeSectionId);
      } catch (err) {
        setError("Failed to load classes.");
      } finally {
        setLoadingClasses(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (gradeSectionId === null) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setSaveError(null);
      try {
        const data = await fetchClassTimetable(gradeSectionId);
        if (!cancelled) {
          setTimetable(data);
          setDraft(toMap(data.slots));
        }
      } catch (err) {
        if (!cancelled) {
          setTimetable(null);
          setDraft({});
          setError(messageOf(err, "Failed to load the timetable."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [gradeSectionId]);

  const saved = useMemo(() => toMap(timetable?.slots ?? []), [timetable]);

  /** Cells whose assignment differs from the saved week (added, changed or cleared). */
  const changedCells = useMemo(() => {
    const keys = new Set([...Object.keys(saved), ...Object.keys(draft)]);
    return Array.from(keys).filter((key) => saved[key] !== draft[key]);
  }, [saved, draft]);
  const dirty = changedCells.length > 0;

  const assign = (day: DayOfWeek, periodId: number, assignmentId: number) =>
    setDraft((prev) => ({ ...prev, [cellKey(day, periodId)]: assignmentId }));

  const clear = (day: DayOfWeek, periodId: number) =>
    setDraft((prev) => {
      const next = { ...prev };
      delete next[cellKey(day, periodId)];
      return next;
    });

  const discard = () => {
    setDraft(saved);
    setSaveError(null);
  };

  /** Returns true when saved; on failure the changes stay on screen and `saveError` says why. */
  const save = async (): Promise<boolean> => {
    if (gradeSectionId === null) return false;
    setSaving(true);
    setSaveError(null);
    try {
      const slots: TimetableSlot[] = Object.entries(draft).map(([key, assignmentId]) => {
        const [day, periodId] = key.split("|");
        return { day: day as DayOfWeek, periodId: Number(periodId), assignmentId };
      });
      const result = await saveClassTimetable(gradeSectionId, slots);
      setTimetable(result);
      setDraft(toMap(result.slots));
      return true;
    } catch (err) {
      setSaveError(messageOf(err, "Failed to save the timetable. Your changes are still on screen."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  /** Switches class at once, or asks first (pendingClassId) when there are unsaved changes. */
  const selectClass = (id: number) => {
    if (id === gradeSectionId) return;
    if (dirty) setPendingClassId(id);
    else setGradeSectionId(id);
  };
  const confirmSwitch = () => {
    if (pendingClassId !== null) setGradeSectionId(pendingClassId);
    setPendingClassId(null);
  };
  const cancelSwitch = () => setPendingClassId(null);

  return {
    grades,
    gradeSectionId,
    pendingClassId,
    timetable,
    draft,
    changedCells,
    dirty,
    loadingClasses,
    loading,
    saving,
    error,
    saveError,
    actions: { selectClass, confirmSwitch, cancelSwitch, assign, clear, discard, save },
  };
};
